import PageShell from "@/components/site/PageShell";
import SEO from "@/components/site/SEO";
import JsonLd, { buildBreadcrumbs } from "@/components/site/JsonLd";
import TrustBand from "@/components/site/TrustBand";
import { Link } from "react-router-dom";
import { Plane, Compass, ArrowRight, CheckCircle2 } from "lucide-react";
import { whatsappHref } from "@/lib/format";

const AIRPORT_POINTS = [
  "Recogida puntual con 15 min de anticipación",
  "Seguimiento del vuelo — si tu vuelo se retrasa, esperamos sin cargo extra",
  "Terminal Puente Aéreo y El Dorado T1 / T2",
  "Traslados corporativos y familiares",
];
const TOUR_POINTS = [
  "City tours guiados por Bogotá",
  "Salidas a Villa de Leyva, Guatavita, Zipaquirá, Melgar, Anapoima",
  "Alquiler por horas o día completo",
  "Van, buseta o bus según el grupo",
];

export default function AeropuertoTours() {
  return (
    <PageShell testid="page-aeropuerto-tours">
      <SEO
        title="Transporte al Aeropuerto y Tours en Bogotá | Líneas Doradas"
        description="Traslados puntuales hacia y desde el aeropuerto, y tours turísticos dentro y fuera de Bogotá en van o buseta. Grupos desde 5 pasajeros."
      />
      <JsonLd
        id="breadcrumb-jsonld"
        data={buildBreadcrumbs([
          { name: "Inicio", url: "https://www.transporteenvanesybuses.com/" },
          { name: "Servicios", url: "https://www.transporteenvanesybuses.com/servicios/aeropuerto-y-tours" },
          { name: "Aeropuerto y Tours", url: "https://www.transporteenvanesybuses.com/servicios/aeropuerto-y-tours" },
        ])}
      />
      <JsonLd
        id="service-jsonld"
        data={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Transporte al aeropuerto y tours en Bogotá",
          "serviceType": "Transporte turístico y traslados",
          "provider": { "@id": "https://www.transporteenvanesybuses.com/#organization" },
          "areaServed": { "@type": "City", "name": "Bogotá" },
          "url": "https://www.transporteenvanesybuses.com/servicios/aeropuerto-y-tours",
          "description": "Traslados puntuales hacia y desde el aeropuerto El Dorado, y tours turísticos dentro y fuera de Bogotá en van o buseta.",
        }}
      />

      <section className="relative pt-16 md:pt-24 pb-16 md:pb-20">
        <div className="orb-gold w-[500px] h-[500px] left-[-140px] top-[80px]" />
        <div className="max-w-7xl mx-auto px-5 md:px-8 relative">
          <div className="text-[#E8C87A] text-xs uppercase tracking-[0.35em] mb-4">Servicio · 01</div>
          <h1 className="font-display text-4xl md:text-6xl leading-[1.05] text-[#F5EFE0] max-w-4xl">
            Traslados al <span className="italic text-gold-grad">aeropuerto</span> y tours dentro y fuera de Bogotá
          </h1>
          <p className="text-[#F5EFE0]/70 mt-5 max-w-2xl text-base md:text-lg leading-relaxed">
            Combinamos puntualidad ejecutiva con experiencia turística. Perfecto para grupos de negocios, familias, agencias y viajeros que quieren llegar tranquilos y disfrutar la ruta.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/cotizar" data-testid="cta-cotizar" className="btn-gold px-7 py-3 rounded-full text-sm">Cotizar ahora</Link>
            <a href={whatsappHref("573123616674", "Hola, quiero información de traslado aeropuerto o tour")} target="_blank" rel="noreferrer" className="btn-ghost-gold px-7 py-3 rounded-full text-sm">
              Preguntar por WhatsApp
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 border-t border-[#3A3229]/60">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-10">
          <div className="card-lux p-8">
            <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/12 border border-[#D4AF37]/35 flex items-center justify-center mb-6">
              <Plane size={26} className="text-[#E8C87A]" />
            </div>
            <div className="text-[#E8C87A] text-xs uppercase tracking-[0.3em] mb-2">Aeropuerto</div>
            <h2 className="font-display text-3xl md:text-4xl text-[#F5EFE0] mb-4">Traslados al aeropuerto El Dorado</h2>
            <p className="text-[#F5EFE0]/65 leading-relaxed mb-6">
              Servicio confiable para llegar a tu vuelo a tiempo o ser recibido apenas aterrizas. Precios cerrados desde $90.000 (grupo hasta 5 personas dentro de la ciudad).
            </p>
            <ul className="space-y-3">
              {AIRPORT_POINTS.map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm text-[#F5EFE0]/80">
                  <CheckCircle2 size={16} className="text-[#E8C87A] mt-0.5 shrink-0" /> {p}
                </li>
              ))}
            </ul>
          </div>

          <div className="card-lux p-8">
            <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/12 border border-[#D4AF37]/35 flex items-center justify-center mb-6">
              <Compass size={26} className="text-[#E8C87A]" />
            </div>
            <div className="text-[#E8C87A] text-xs uppercase tracking-[0.3em] mb-2">Tours</div>
            <h2 className="font-display text-3xl md:text-4xl text-[#F5EFE0] mb-4">Tours dentro y fuera de Bogotá</h2>
            <p className="text-[#F5EFE0]/65 leading-relaxed mb-6">
              Recorridos turísticos con alquiler por horas o día completo. Perfectos para agencias, empresas que reciben visitantes o grupos familiares en escapada.
            </p>
            <ul className="space-y-3">
              {TOUR_POINTS.map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm text-[#F5EFE0]/80">
                  <CheckCircle2 size={16} className="text-[#E8C87A] mt-0.5 shrink-0" /> {p}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <TrustBand />

      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
          <div className="text-[#E8C87A] text-xs uppercase tracking-[0.35em] mb-4">Explora más</div>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/servicios/transporte-turistico-colombia" className="btn-ghost-gold px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2">
              Transporte por toda Colombia <ArrowRight size={13} />
            </Link>
            <Link to="/servicios/rutas" className="btn-ghost-gold px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2">
              Rutas empresariales y escolares <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
